import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../i18n';
import './EnrichedText.css';

/**
 * Sub-componente para manejar la interacción de cada fragmento enriquecido.
 */
const TOOLTIP_MOBILE_GUTTER = 12;
const TOOLTIP_DESKTOP_GUTTER = 16;
const TOOLTIP_MAX_WIDTH = 320;
const TOOLTIP_GAP = 8;
const TOOLTIP_MIN_HEIGHT = 96;
const SAFE_SOURCE_PROTOCOLS = new Set(['http:', 'https:']);

const getSafeSourceUrl = (link) => {
  if (typeof link !== 'string') return null;

  const trimmedLink = link.trim();
  if (!trimmedLink) return null;

  try {
    const url = new URL(trimmedLink);
    return SAFE_SOURCE_PROTOCOLS.has(url.protocol) ? url.href : null;
  } catch (_) {
    return null;
  }
};

const getSafeSourceLinks = (links) => (
  Array.isArray(links) ? links.map(getSafeSourceUrl).filter(Boolean) : []
);

const getPointLinks = (point) => {
  const directLinks = getSafeSourceLinks(point.links);
  if (directLinks.length > 0 || Object.prototype.hasOwnProperty.call(point, 'links')) {
    return directLinks;
  }

  return getSafeSourceLinks(point.meta?.sources);
};

const normalizeAskingPoints = (text, askingPoints) => {
  if (typeof text !== 'string' || !Array.isArray(askingPoints)) return [];

  const searchableText = text.toLowerCase();
  return askingPoints.reduce((normalized, point) => {
    if (!point || typeof point.fragment !== 'string' || typeof point.context !== 'string') {
      return normalized;
    }

    const fragment = point.fragment.trim();
    const context = point.context.trim();

    if (!fragment || !context || !searchableText.includes(fragment.toLowerCase())) {
      return normalized;
    }

    normalized.push({
      ...point,
      fragment,
      context,
      links: getPointLinks(point),
    });
    return normalized;
  }, []);
};

const getTooltipGutter = (viewportWidth) => (viewportWidth < 768 ? TOOLTIP_MOBILE_GUTTER : TOOLTIP_DESKTOP_GUTTER);

const isMousePointer = (event) => event.pointerType === 'mouse';
const isTouchLikePointer = (event) => event.pointerType === 'touch' || event.pointerType === 'pen';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const calculateTooltipLayout = (wrapperElement, tooltipElement) => {
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
  const gutter = getTooltipGutter(viewportWidth);
  const fallbackWidth = Math.min(TOOLTIP_MAX_WIDTH, Math.max(0, viewportWidth - gutter * 2));

  if (!wrapperElement) {
    return { left: `${gutter}px`, top: `${gutter}px`, arrowLeft: '50%', width: fallbackWidth, maxHeight: 'none', placement: 'bottom' };
  }

  const wrapperRect = wrapperElement.getBoundingClientRect();
  const tooltipRect = tooltipElement?.getBoundingClientRect?.();
  const tooltipWidth = Math.min(TOOLTIP_MAX_WIDTH, Math.max(0, viewportWidth - gutter * 2));
  const measuredHeight = tooltipRect?.height || TOOLTIP_MIN_HEIGHT;
  const fragmentCenter = wrapperRect.left + wrapperRect.width / 2;
  const maxLeft = Math.max(gutter, viewportWidth - tooltipWidth - gutter);
  const tooltipLeft = clamp(fragmentCenter - tooltipWidth / 2, gutter, maxLeft);
  const arrowLeft = clamp(fragmentCenter - tooltipLeft, 16, Math.max(16, tooltipWidth - 16));
  const spaceBelow = viewportHeight - wrapperRect.bottom - TOOLTIP_GAP - gutter;
  const spaceAbove = wrapperRect.top - TOOLTIP_GAP - gutter;
  const shouldOpenBelow = measuredHeight <= spaceBelow || spaceBelow >= spaceAbove;
  const placement = shouldOpenBelow ? 'bottom' : 'top';
  const availableHeight = Math.max(TOOLTIP_MIN_HEIGHT, placement === 'bottom' ? spaceBelow : spaceAbove);
  const maxHeight = Math.min(Math.max(0, viewportHeight - gutter * 2), availableHeight);
  const desiredTop = placement === 'bottom'
    ? wrapperRect.bottom + TOOLTIP_GAP
    : wrapperRect.top - Math.min(measuredHeight, maxHeight) - TOOLTIP_GAP;
  const maxTop = Math.max(gutter, viewportHeight - Math.min(measuredHeight, maxHeight) - gutter);
  const tooltipTop = clamp(desiredTop, gutter, maxTop);

  return {
    left: `${tooltipLeft}px`,
    top: `${tooltipTop}px`,
    arrowLeft: `${arrowLeft}px`,
    width: tooltipWidth,
    maxHeight: `${maxHeight}px`,
    placement,
  };
};

const AskingPoint = ({ fragment, context, links = [], tooltipId }) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipLayout, setTooltipLayout] = useState({
    left: '12px',
    top: '12px',
    arrowLeft: '50%',
    width: TOOLTIP_MAX_WIDTH,
    maxHeight: 'none',
    placement: 'bottom',
  });
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const lastTouchToggleRef = useRef(0);
  const safeLinks = useMemo(() => getSafeSourceLinks(links), [links]);

  const clearCloseTimeout = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  const openTooltip = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(true);
  }, [clearCloseTimeout]);

  const closeTooltip = useCallback(() => {
    clearCloseTimeout();
    if (typeof window === 'undefined') {
      setIsOpen(false);
      return;
    }
    closeTimeoutRef.current = window.setTimeout(() => setIsOpen(false), 80);
  }, [clearCloseTimeout]);

  const closeTooltipNow = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(false);
  }, [clearCloseTimeout]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const rafIds = [];
    let resizeObserver;

    const updateLayout = () => {
      setTooltipLayout(calculateTooltipLayout(wrapperRef.current, tooltipRef.current));
    };

    const queueLayout = () => {
      const rafId = window.requestAnimationFrame(updateLayout);
      rafIds.push(rafId);
    };

    updateLayout();
    queueLayout();
    const secondFrame = window.requestAnimationFrame(queueLayout);
    rafIds.push(secondFrame);

    if (typeof window.ResizeObserver !== 'undefined' && tooltipRef.current) {
      resizeObserver = new window.ResizeObserver(updateLayout);
      resizeObserver.observe(tooltipRef.current);
    }

    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, true);
    window.addEventListener('orientationchange', updateLayout);

    return () => {
      rafIds.forEach((rafId) => window.cancelAnimationFrame(rafId));
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout, true);
      window.removeEventListener('orientationchange', updateLayout);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    const isInsideAskingPoint = (target) => {
      if (!target || typeof Node === 'undefined' || !(target instanceof Node)) return false;
      if (wrapperRef.current?.contains(target)) return true;
      if (!tooltipRef.current) return true;
      return tooltipRef.current.contains(target);
    };

    const handleOutsidePointerDown = (event) => {
      if (isInsideAskingPoint(event.target)) return;
      closeTooltipNow();
    };

    const handleOutsideFocus = (event) => {
      if (isInsideAskingPoint(event.target)) return;
      closeTooltipNow();
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') closeTooltipNow();
    };

    const pointerEventName = typeof window !== 'undefined' && window.PointerEvent ? 'pointerdown' : 'mousedown';
    document.addEventListener(pointerEventName, handleOutsidePointerDown, true);
    document.addEventListener('focusin', handleOutsideFocus, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener(pointerEventName, handleOutsidePointerDown, true);
      document.removeEventListener('focusin', handleOutsideFocus, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeTooltipNow, isOpen]);

  useLayoutEffect(() => (
    () => clearCloseTimeout()
  ), [clearCloseTimeout]);

  const toggleTooltip = useCallback(() => {
    clearCloseTimeout();
    setIsOpen((wasOpen) => !wasOpen);
  }, [clearCloseTimeout]);

  const isEventFromWrapperDom = useCallback((event) => {
    const target = event.target;
    return Boolean(
      wrapperRef.current &&
      typeof Node !== 'undefined' &&
      target instanceof Node &&
      wrapperRef.current.contains(target)
    );
  }, []);

  const stopTooltipPropagation = (event) => {
    event.stopPropagation();
  };

  const handlePointerEnter = (event) => {
    if (isMousePointer(event)) openTooltip();
  };

  const handlePointerLeave = (event) => {
    if (isMousePointer(event)) closeTooltip();
  };

  const handlePointerUp = (event) => {
    if (!isTouchLikePointer(event)) return;
    if (!isEventFromWrapperDom(event)) return;
    lastTouchToggleRef.current = Date.now();
    event.stopPropagation();
    toggleTooltip();
  };

  const handleClick = (event) => {
    if (!isEventFromWrapperDom(event)) return;

    const hasPointerEvents = typeof window !== 'undefined' && Boolean(window.PointerEvent);
    if (hasPointerEvents) {
      if (Date.now() - lastTouchToggleRef.current < 700) {
        event.stopPropagation();
      }
      return;
    }

    event.stopPropagation();
    toggleTooltip();
  };

  const tooltip = isOpen && typeof document !== 'undefined' ? createPortal(
    <AnimatePresence>
      <motion.div
        ref={tooltipRef}
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        className={`enriched-tooltip enriched-tooltip--${tooltipLayout.placement}`}
        style={{
          '--tooltip-left': tooltipLayout.left,
          '--tooltip-top': tooltipLayout.top,
          '--tooltip-width': `${tooltipLayout.width}px`,
          '--tooltip-max-height': tooltipLayout.maxHeight,
          '--tooltip-arrow-left': tooltipLayout.arrowLeft,
        }}
        id={tooltipId}
        role="tooltip"
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerDown={stopTooltipPropagation}
        onPointerUp={stopTooltipPropagation}
        onClick={stopTooltipPropagation}
        onMouseDown={stopTooltipPropagation}
      >
        <div className="tooltip-content">
          <p className="tooltip-context">{context}</p>
          {safeLinks.length > 0 && (
            <div className="tooltip-links">
              {safeLinks.map((link, idx) => (
                <a
                  key={`${link}-${idx}`}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tooltip-link"
                  onPointerDown={stopTooltipPropagation}
                  onPointerUp={stopTooltipPropagation}
                  onClick={stopTooltipPropagation}
                >
                  🔗 {t('mutantBox.source')} {safeLinks.length > 1 ? idx + 1 : ''}
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="tooltip-arrow" />
      </motion.div>
    </AnimatePresence>,
    document.body
  ) : null;

  return (
    <span
      ref={wrapperRef}
      className="enriched-fragment-wrapper"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
      onFocus={openTooltip}
      tabIndex={0}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      aria-controls={isOpen ? tooltipId : undefined}
    >
      <span className="enriched-fragment-text">{fragment}</span>

      <AnimatePresence>
        {isOpen && (
          <motion.span
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            className="asking-dot"
          >
            ?
          </motion.span>
        )}
      </AnimatePresence>

      {tooltip}
    </span>
  );
};

/**
 * Componente principal EnrichedText.
 * Parsea el texto e inyecta AskingPoints donde coincidan los fragmentos.
 */
const EnrichedText = ({ text, askingPoints = [] }) => {
  const normalizedPoints = useMemo(() => normalizeAskingPoints(text, askingPoints), [text, askingPoints]);

  const parts = useMemo(() => {
    if (!text) return [];
    if (normalizedPoints.length === 0) return [text];

    // Ordenar por longitud descendente para evitar que fragmentos cortos rompan los largos
    const sortedPoints = [...normalizedPoints].sort((a, b) => b.fragment.length - a.fragment.length);

    // Escapar caracteres especiales para el Regex
    const escapedFragments = sortedPoints.map(p =>
      p.fragment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );

    // Crear el Regex con grupos de captura para que split mantenga los delimitadores (fragmentos)
    const regex = new RegExp(`(${escapedFragments.join('|')})`, 'gi');

    return text.split(regex);
  }, [text, normalizedPoints]);

  if (!text) return null;

  return (
    <div className="enriched-text-container">
      {parts.map((part, index) => {
        // Buscar si esta parte coincide con algún fragmento (case-insensitive)
        const matchingPoint = normalizedPoints.find(
          p => p.fragment.toLowerCase() === part.toLowerCase()
        );

        if (matchingPoint) {
          return (
            <AskingPoint
              key={`${index}-${part}`}
              fragment={part}
              context={matchingPoint.context}
              links={matchingPoint.links}
              tooltipId={`asking-tooltip-${index}`}
            />
          );
        }

        // Si no es un match, lo devolvemos como texto normal (dentro de un fragmento para evitar problemas de key)
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </div>
  );
};

export default EnrichedText;
